import React, { useEffect, useReducer, useState, useContext } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import AddIcon from '@mui/icons-material/Add';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    backgroundColor: theme.palette.background.default,
    border: "none",
    ...theme.scrollbarStyles,
  },

  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(3),
    borderRadius: "0 0 20px 20px",
    color: "white",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  headerTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    fontSize: "1rem",
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  addButton: {
    background: theme.palette.background.paper,
    color: "#6517ab",
    borderRadius: "12px",
    padding: "10px 24px",
    fontWeight: "700",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.type === "light" ? "#f0f0f0" : "rgba(255, 255, 255, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    },
  },

  tabContainer: {
    marginTop: theme.spacing(2),
  },
  tabs: {
    minHeight: "40px",
    "& .MuiTabs-indicator": {
      backgroundColor: "white",
      height: "3px",
      borderRadius: "3px",
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.7)",
    minWidth: "120px",
    minHeight: "40px",
    "&.Mui-selected": {
      color: "white",
    },
  },
  premiumCard: {
    borderRadius: "20px",
    transition: "all 0.3s ease",
    border: "none",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    background: theme.palette.background.paper,
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 15px 30px rgba(101, 23, 171, 0.12)",
    },
  },

  colorIndicator: {
    width: 60,
    height: 12,
    margin: "0 auto 16px",
    borderRadius: 6,
    border: "2px solid rgba(0,0,0,0.05)",
  },
  iconButton: {
    borderRadius: "10px",
    padding: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  tableRow: {
    transition: "all 0.2s",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.palette.type === "light" ? "rgba(101, 23, 171, 0.05)" : "rgba(255, 255, 255, 0.05)",
    },
  },

}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];
    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });
    return [...state, ...newQueues];
  }
  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);
    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }
  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;
  const [tabValue, setTabValue] = useState("table");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: +data.queueId });
      }
    };
    socket.on(`company-${companyId}-queue`, onQueueEvent);
    return () => {
      socket.off(`company-${companyId}-queue`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Fila deletada com Sucesso"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderCardActions = (queue) => (
    <Box display="flex" gap={1} justifyContent="center" width="100%" mt={tabValue === "grid" ? 2 : 0}>
      <IconButton
        size="small"
        onClick={() => handleEditQueue(queue)}
        style={{ 
          backgroundColor: theme.palette.type === 'dark' ? "rgba(25, 118, 210, 0.1)" : "#e3f2fd", 
          color: "#1976d2" 
        }}
        className={classes.iconButton}
        title="Editar"
      >
        <Edit fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => {
          setSelectedQueue(queue);
          setConfirmModalOpen(true);
        }}
        style={{ 
          backgroundColor: theme.palette.type === 'dark' ? "rgba(211, 47, 47, 0.1)" : "#ffebee", 
          color: "#d32f2f" 
        }}
        className={classes.iconButton}
        title="Excluir"
      >
        <DeleteOutline fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        onEdit={(res) => {
          if (res) {
            setTimeout(() => {
              handleEditQueue(res)
            }, 500)
          }
        }}
      />
      {user.profile === "user" ?
        <ForbiddenPage />
        :
        <>
          <Box className={classes.headerContainer}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h5" className={classes.headerTitle}>Filas & Setores</Typography>
                <Typography className={classes.headerSubtitle}>Organize seu atendimento com departamentos eficientes</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent={isMobile ? "center" : "flex-end"}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    className={classes.addButton}
                    onClick={handleOpenQueueModal}
                  >
                    Adicionar Fila
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <div className={classes.tabContainer}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                className={classes.tabs}
                classes={{ indicator: classes.tabIndicator }}
              >
                <Tab label="Lista" value="table" className={classes.tab} />
                <Tab label="Grade" value="grid" className={classes.tab} />
              </Tabs>
            </div>
          </Box>

          <Paper className={classes.mainPaper} elevation={0}>
            {tabValue === "table" && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Cor</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell align="center">Ordenação</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queues.map((queue) => (
                    <TableRow key={queue.id} className={classes.tableRow}>
                      <TableCell align="center">
                        <div
                          style={{
                            backgroundColor: queue.color,
                            width: 30,
                            height: 15,
                            margin: "0 auto",
                            borderRadius: 4,
                            border: "1px solid rgba(0,0,0,0.1)"
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" style={{ fontWeight: 500, color: theme.palette.text.primary }}>
                          {queue.name}
                        </Typography>

                        <Typography variant="caption" color="textSecondary">
                          ID: {queue.id}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={queue.orderQueue}
                          size="small"
                          variant="outlined"
                          style={{ fontWeight: 700, color: "#6517ab", borderColor: "#6517ab" }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {renderCardActions(queue)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={4} />}
                </TableBody>
              </Table>
            )}

            {tabValue === "grid" && (
              <Grid container spacing={3}>
                {queues.map((queue) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={queue.id}>
                    <Card className={classes.premiumCard}>
                      <CardContent style={{ textAlign: 'center', flexGrow: 1 }}>
                        <div
                          className={classes.colorIndicator}
                          style={{ backgroundColor: queue.color }}
                        />
                        <Typography variant="h6" style={{ fontWeight: 700, color: theme.palette.text.primary }} gutterBottom>
                          {queue.name}
                        </Typography>

                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          ID: {queue.id}
                        </Typography>

                        <Divider style={{ margin: '16px 0' }} />

                        <Box display="flex" justifyContent="space-between" alignItems="center" px={1}>
                          <Typography variant="caption" color="textSecondary">
                            Ordenação:
                          </Typography>
                          <Chip
                            label={queue.orderQueue}
                            size="small"
                            style={{ backgroundColor: "#f0f0f0", fontWeight: 700 }}
                          />
                        </Box>

                        {renderCardActions(queue)}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {loading && (
                  <Grid item xs={12} align="center">
                    <CircularProgress color="primary" />
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>
        </>}
    </MainContainer>
  );
};

export default Queues;
